import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap } from "lucide-react";
import { CountryFlag } from "@/components/ui/CountryFlag";
import { buddies, initials, type Buddy } from "@/data/buddies";
import { EASE_EXPO, KineticHeading, Magnetic, Reveal, TiltCard } from "@/components/motion/primitives";

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL = "#2EE6D6";
const TEAL_SOFT = "#CFF9F4";
const OAK = "#C6872F";
const SECONDARY = "#46504D";
const MUTED = "#69716E";

/**
 * A buddy as a photo card: the portrait carries the name and the move
 * (country to city); below it only facts that are true today.
 */
function BuddyCard({ buddy }: { buddy: Buddy }) {
  return (
    <TiltCard className="h-full overflow-hidden bg-white" style={{ borderRadius: 28, boxShadow: "var(--shadow-calm)" }}>
      <article className="flex h-full flex-col">
        <div className="relative aspect-[4/5] flex-1 overflow-hidden" style={{ backgroundColor: TEAL_SOFT }}>
          {buddy.photoUrl ? (
            <img
              src={buddy.photoUrl}
              alt={`${buddy.name}, Migrada buddy from ${buddy.countryName}, in ${buddy.city}`}
              loading="lazy"
              width={960}
              height={1200}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[64px] font-black" style={{ color: BRAND_DARK }} aria-hidden="true">
              {initials(buddy.name)}
            </div>
          )}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2" style={{ background: "linear-gradient(to top, rgba(0,26,24,0.82), rgba(0,26,24,0))" }} />
          <div className="absolute inset-x-0 bottom-0 p-6">
            <span
              className="inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-[13px] font-bold text-white"
              style={{ backgroundColor: "rgba(255,255,255,0.16)", backdropFilter: "blur(6px)" }}
            >
              <CountryFlag code={buddy.countryCode} size="sm" circle animate={false} />
              Buddy for {buddy.countryName}
            </span>
            <h3 className="mt-3 text-[32px] font-black leading-none tracking-[-0.03em] text-white">{buddy.name}</h3>
            <p className="mt-2 text-[15px] font-semibold" style={{ color: "rgba(255,255,255,0.86)" }}>
              {buddy.countryName} to {buddy.city}
              {buddy.movedYear ? `, since ${buddy.movedYear}` : ""}
            </p>
          </div>
        </div>
        <div className="px-6 py-4">
          {buddy.programme && (
            <p className="text-[15px] font-bold leading-snug" style={{ color: INK }}>
              {buddy.programme}
            </p>
          )}
          <p className="mt-0.5 flex items-center gap-1.5 text-[14px]" style={{ color: MUTED }}>
            <GraduationCap className="h-4 w-4 shrink-0" style={{ color: OAK }} aria-hidden="true" />
            {buddy.university}
          </p>
          {buddy.languages && buddy.languages.length > 0 && (
            <p className="mt-1 text-[14px]" style={{ color: MUTED }}>
              Speaks {buddy.languages.join(", ")}
            </p>
          )}
        </div>
      </article>
    </TiltCard>
  );
}

/** Flags that orbit an empty seat: the place a new buddy takes. */
const ORBIT = ["KZ", "IN", "TR", "NG", "UZ", "BR"];

function EmptySeat() {
  return (
    <div className="relative mx-auto my-2 h-[150px] w-[150px] shrink-0" aria-hidden="true">
      <div className="absolute inset-0 rounded-full border-2 border-dashed" style={{ borderColor: "rgba(46,230,214,0.45)" }} />
      <motion.div className="absolute inset-0" animate={{ rotate: 360 }} transition={{ duration: 28, repeat: Infinity, ease: "linear" }}>
        {ORBIT.map((code, i) => {
          const a = (i / ORBIT.length) * Math.PI * 2;
          return (
            <motion.span
              key={code}
              className="absolute rounded-full bg-white p-[2px]"
              style={{ left: `calc(50% + ${Math.cos(a) * 75}px - 12px)`, top: `calc(50% + ${Math.sin(a) * 75}px - 12px)` }}
              animate={{ rotate: -360 }}
              transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
            >
              <CountryFlag code={code} size="sm" circle animate={false} />
            </motion.span>
          );
        })}
      </motion.div>
      <motion.div
        className="absolute inset-[34px] flex items-center justify-center rounded-full text-[22px] font-black tracking-[-0.03em]"
        style={{ backgroundColor: TEAL, color: BRAND_DARK }}
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      >
        You?
      </motion.div>
    </div>
  );
}

/**
 * Phones get a swipe row (the next card peeks in, snap per card); from sm up
 * it is a grid. The row reveals as one, staggered, because a card that only
 * peeks in from the side would never count as "in view" on its own.
 */
const SLIDE = "w-[82%] shrink-0 snap-start sm:w-auto";
const RISE = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE_EXPO } } };

/**
 * The join tile fills the rest of the last row: a narrow tile stacks its text
 * and the empty seat, a wide one sets them side by side. Literal class names,
 * so Tailwind keeps them.
 */
function joinTile(count: number) {
  const sm = count % 2 === 0 ? 2 : 1;
  const lg = 3 - (count % 3);
  return {
    span: [sm === 2 ? "sm:col-span-2" : "", lg === 1 ? "lg:col-span-1" : lg === 2 ? "lg:col-span-2" : "lg:col-span-3"].join(" "),
    inner: [sm === 2 ? "sm:flex-row sm:items-center" : "", lg === 1 ? "lg:flex-col-reverse lg:items-start" : "lg:flex-row lg:items-center"].join(" "),
  };
}

/** The open seat: students already in Austria can join the team. */
function JoinCard({ inner = "sm:flex-row sm:items-center" }: { inner?: string }) {
  return (
    <div className="relative flex h-full flex-col justify-center overflow-hidden p-7 sm:p-9" style={{ borderRadius: 28, backgroundColor: BRAND_DARK }}>
      <div className={`flex flex-col gap-6 ${inner}`}>
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center gap-2 text-[14px] font-bold uppercase tracking-[0.08em]" style={{ color: TEAL }}>
            <GraduationCap className="h-5 w-5" aria-hidden="true" />
            Studying in Austria?
          </span>
          <p className="mt-3 text-[22px] font-extrabold leading-snug tracking-[-0.02em] text-white">Join the team and help students from your country make the move you already made.</p>
        </div>
        <EmptySeat />
      </div>
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3, ease: EASE_EXPO }}>
        <Magnetic className="mt-6">
          <Link
            to={{ pathname: "/community", hash: "#buddy" }}
            className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[17px] font-bold transition-[filter,transform] hover:brightness-95 active:scale-95"
            style={{ backgroundColor: TEAL, color: BRAND_DARK }}
          >
            Join the team
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
        </Magnetic>
      </motion.div>
    </div>
  );
}

/**
 * Buddies. Real people only, with their consent recorded in the app; the list
 * lives in src/data/buddies.ts. Buddies are volunteers on the Migrada team. While the list is
 * empty, the section says so plainly and invites students already in Austria
 * to join the team, which is also true and useful.
 */
export function Buddies() {
  return (
    <section id="buddies" className="scroll-mt-24 py-20 md:py-28" style={{ backgroundColor: "var(--color-bg-calm)" }}>
      <div className="mx-auto max-w-[1120px] px-5 sm:px-8">
        <div className="max-w-2xl">
          <KineticHeading text="Buddies: students who already moved" className="font-black leading-[0.95] tracking-[-0.04em] [text-wrap:balance]" style={{ fontSize: "var(--text-h2)", color: INK }} />
          <Reveal delay={0.2}>
            <p className="mt-5 text-[18px] leading-relaxed" style={{ color: SECONDARY }}>
              A buddy moved to Austria from your country. They answer your questions in the community and share what they learned, as volunteers on the Migrada team.
            </p>
          </Reveal>
        </div>

        {buddies.length > 0 ? (
          <motion.div
            className="scrollbar-hide -mx-5 mt-12 flex snap-x snap-mandatory scroll-px-5 gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-3"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          >
            {buddies.map((b) => (
              <motion.div key={b.id} variants={RISE} className={SLIDE}>
                <BuddyCard buddy={b} />
              </motion.div>
            ))}
            <motion.div variants={RISE} className={`${SLIDE} ${joinTile(buddies.length).span}`}>
              <JoinCard inner={joinTile(buddies.length).inner} />
            </motion.div>
          </motion.div>
        ) : (
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            <Reveal className="bg-white p-7 sm:p-9" style={{ borderRadius: 28 }}>
              <p className="text-[22px] font-extrabold leading-snug tracking-[-0.02em]" style={{ color: INK }}>
                We are choosing our first buddies right now.
              </p>
              <p className="mt-3 text-[16px] leading-relaxed" style={{ color: SECONDARY }}>
                Only real students will appear here, with their real names and universities and only with their consent. No stock photos and no made-up profiles. Until then, your plan is written by
                our team.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <JoinCard />
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}
